import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Archive } from 'lucide-react';

import PageHeader from '../../../components/page/PageHeader';
import { extractWikiPages } from '../../../hooks/useWikiV2';
import WikiFrame from '../shared/WikiFrame';
import WikiIcon, { WikiIconSquare } from '../shared/WikiIcon';
import { WikiEmpty, WikiError, WikiLoading } from '../shared/WikiStates';
import useOpenWikiAsk from '../shared/useOpenWikiAsk';
import useWikiRole from '../shared/useWikiRole';
import WikiAccessPanel from '../access/WikiAccessPanel';
import WikiActionsMenu from '../shared/WikiActionsMenu';
import { archiveCategoryConfirm, refreshAfterArchive } from '../shared/wikiManageActions';
import { WIKI_MANAGE_COPY, WIKI_MODAL_TOASTS } from '../modals/wikiModalCopy';
import { useWikiCategories, useWikiCategory, useWikiQuery, wikiKeys } from '../shared/useWikiQueries';
import { categoryColors, dateLabel, plural, versionLabel } from '../shared/wikiFormat';
import { wikiPaths } from '../shared/wikiRoutes';
import { wikiUi } from '../shared/wikiUi';
import { isAckable, pageVersion } from '../page/wikiPageCopy';
import WikiCrumbs from './WikiCrumbs';
import useWikiPlanAllows, { WIKI_PLAN_FEATURES, categoryAssistantOn } from './wikiPlan';
import {
    EMPTY_BODY,
    EMPTY_TITLE,
    NOT_FOUND_BODY,
    NOT_FOUND_TITLE,
    QUOTE_EXACT_NOTE,
    SUBCATEGORIES_LABEL,
    accessLine,
    askLabel,
    ownerLine,
    pagesLabel,
} from './wikiCategoryCopy';
import './wikiCategory.css';

/**
 * Category view — `/wiki/c/:id`, where `categoryId` is a top-level category
 * OR a subcategory (spec B §1.2). Frame + type = the CRM Dashboard
 * (REBUILD-PLAN §1); structure, copy, icons, colours and interactions = the
 * prototype (`wkIsCat`).
 *
 *   crumbs     Wiki / Category [/ Subcategory]
 *   header     44px icon tile (the PARENT's in a sub view) · PageHeader (name,
 *              description, actions) · meta: access line + owner (always the
 *              parent's) + "Answers quote these pages word for word"
 *   actions    Ask {Category} (everyone) · New subcategory (top level only) ·
 *              Import · New page — creation is gated on `canEdit` AND edit
 *              access on the location (Owen: Editors create categories,
 *              subcategories and pages; the API refuses the rest). The plan
 *              hides Import (`wiki_import`) and Ask {Category}
 *              (`wiki_category_assistants`) — PLAN-CP; unknown plan → shown.
 *              Ask {Category} also needs the category's (and parent's)
 *              `assistant_enabled` — the "Category assistant" switch
 *              ⋯ menu (#872): Settings (`canEdit` + edit access, the PATCH
 *              rule) · Archive (Wiki admins; the confirmation counts what
 *              goes with it, then lands on the parent or Wiki home)
 *   SUBCATEGORIES   cards (top level only)
 *   Pages      the DIRECT pages (`direct=1`), prototype data order
 *              (`sort=created`), with Required / Draft / Acknowledged /
 *              Needs you pills, owner and updated date; else the empty state
 */
export default function WikiCategory({ categoryId }) {
    const navigate = useNavigate();
    const role = useWikiRole();
    const openAsk = useOpenWikiAsk();
    const planAllows = useWikiPlanAllows();
    const categories = useWikiCategories();
    // `access_summary` for the header line (#871). Keyed by the route prop and
    // called before the early returns below (Rules of Hooks); `categoryId` is
    // the shown category or subcategory.
    const detail = useWikiCategory(categoryId);
    const [accessOpen, setAccessOpen] = useState(false);
    const queryClient = useQueryClient();
    const pagesQuery = useWikiQuery(
        wikiKeys.pages({ category: categoryId, direct: 1, sort: 'created' }),
        categoryId
            ? `/wiki/pages?category=${encodeURIComponent(categoryId)}&direct=1&sort=created&limit=500`
            : null,
    );

    if (categories.isLoading) {
        return (
            <WikiFrame testId="wiki-category">
                <div className="wk-card"><WikiLoading label="Loading category…" /></div>
            </WikiFrame>
        );
    }
    if (categories.isError) {
        return (
            <WikiFrame testId="wiki-category">
                <div className="wk-card"><WikiError error={categories.error} onRetry={() => categories.refetch()} /></div>
            </WikiFrame>
        );
    }

    const found = categories.find(categoryId);
    if (!found) {
        return (
            <WikiFrame testId="wiki-category">
                <div className="wk-card" data-testid="wiki-category-not-found">
                    <WikiEmpty
                        icon="folder"
                        title={NOT_FOUND_TITLE}
                        detail={NOT_FOUND_BODY}
                        action={(
                            <button type="button" className="wk-btn wk-btn--secondary wk-btn--sm" onClick={() => navigate(wikiPaths.home())}>
                                <WikiIcon name="house" size={14} /> Wiki home
                            </button>
                        )}
                    />
                </div>
            </WikiFrame>
        );
    }

    const { category: current, isSub } = found;
    const top = isSub ? found.parent : current;
    const subs = isSub ? [] : ((categories.tree.find((row) => row.id === top.id) || {}).subs || []);
    const colors = categoryColors(top);
    const location = { categoryId: top.id, subcategoryId: isSub ? current.id : null };

    // Owen 2026-10-06: Editors create subcategories and pages. Also require edit
    // access on the location so nobody is offered a button the API refuses.
    const canCreatePage = role.canEdit && current.can_edit !== false;
    const canCreateSub = !isSub && role.canEdit && top.can_edit !== false;
    const canImport = canCreatePage && planAllows(WIKI_PLAN_FEATURES.import);
    const canAsk = planAllows(WIKI_PLAN_FEATURES.categoryAssistants)
        && categoryAssistantOn(top, isSub ? current : null);

    const openNewPage = () => wikiUi.openModal('page', { mode: 'write', ...location });
    const openImport = () => wikiUi.openModal('page', { mode: 'import', ...location });
    const openNewSub = () => wikiUi.openModal('subcategory', { parentId: top.id });
    const canSettings = role.canEdit && current.can_edit !== false;
    const openSettings = () => wikiUi.openModal('categorySettings', { category: current });
    const openArchive = () => wikiUi.openModal('confirm', archiveCategoryConfirm({
        category: current,
        onArchived: async () => {
            navigate(isSub ? wikiPaths.category(top.id) : wikiPaths.home());
            wikiUi.toast(WIKI_MODAL_TOASTS.categoryArchived(current.name));
            await refreshAfterArchive(queryClient, wikiKeys.category(current.id));
        },
    }));

    const crumbs = [
        { label: 'Wiki', to: wikiPaths.home() },
        { label: top.name, to: wikiPaths.category(top.id) },
        isSub ? { label: current.name, to: wikiPaths.category(current.id) } : null,
    ];

    const actions = (
        <>
            {role.canAdmin && (
                <button
                    type="button"
                    className="wk-btn wk-btn--secondary"
                    data-testid="wiki-category-access"
                    data-action="wiki.category_access"
                    onClick={() => setAccessOpen(true)}
                >
                    <WikiIcon name="lock" size={15} />
                    Access
                </button>
            )}
            {canAsk && (
                <button
                    type="button"
                    className="wk-btn wk-btn--ask"
                    data-testid="wiki-category-ask"
                    onClick={() => openAsk({ categoryId: top.id })}
                >
                    <WikiIcon name="sparkles" size={15} />
                    {askLabel(top)}
                </button>
            )}
            {canCreateSub && (
                <button type="button" className="wk-btn wk-btn--secondary" data-testid="wiki-category-new-sub" onClick={openNewSub}>
                    <WikiIcon name="folder" size={15} />
                    New subcategory
                </button>
            )}
            {canImport && (
                <button type="button" className="wk-btn wk-btn--secondary" data-testid="wiki-category-import" onClick={openImport}>
                    <WikiIcon name="upload" size={15} />
                    Import
                </button>
            )}
            {canCreatePage && (
                <button type="button" className="wk-btn wk-btn--primary wk-cat-new" data-testid="wiki-category-new-page" onClick={openNewPage}>
                    <WikiIcon name="plus" size={15} />
                    New page
                </button>
            )}
            <WikiActionsMenu
                testId="wiki-category-actions"
                label={WIKI_MANAGE_COPY.menuLabel}
                items={[
                    canSettings && {
                        id: 'settings', label: WIKI_MANAGE_COPY.settings, icon: 'settings',
                        onSelect: openSettings, testId: 'wiki-category-settings', action: 'wiki.category_settings',
                    },
                    role.canAdmin && {
                        id: 'archive', label: WIKI_MANAGE_COPY.archive, icon: Archive, danger: true,
                        onSelect: openArchive, testId: 'wiki-category-archive', action: 'wiki.archive_category',
                    },
                ]}
            />
        </>
    );

    const owner = ownerLine(top);
    const pages = extractWikiPages(pagesQuery.data);

    return (
        <WikiFrame testId="wiki-category" data-category-id={current.id}>
            <div className="wk-cat-top">
                <WikiCrumbs items={crumbs} />
                <div className="wk-cat-head">
                    <WikiIconSquare icon={top.icon} color={colors.color} tint={colors.tint} size={44} />
                    <div className="wk-cat-head-main">
                        <PageHeader
                            title={current.name}
                            subtitle={current.description || null}
                            right={actions}
                            testId="wiki-category-header"
                        />
                        <div className="wk-cat-meta wk-t-meta" data-testid="wiki-category-meta">
                            <span className="wk-cat-meta-item">
                                <WikiIcon name="lock" size={13} />
                                <span data-testid="wiki-category-access-line">
                                    {accessLine(detail.data && detail.data.access_summary) || '\u00a0'}
                                </span>
                            </span>
                            {owner && (
                                <span className="wk-cat-meta-item">
                                    <WikiIcon name="user" size={13} />
                                    {owner}
                                </span>
                            )}
                            {top.answer_mode === 'quote_exact' && (
                                <span className="wk-cat-meta-item wk-cat-meta-item--exact" data-testid="wiki-category-exact">
                                    <WikiIcon name="quote" size={13} />
                                    {QUOTE_EXACT_NOTE}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {subs.length > 0 && (
                <section className="wk-cat-section" aria-labelledby="wk-cat-subs-label" data-testid="wiki-category-subs">
                    <h2 id="wk-cat-subs-label" className="wk-t-label wk-cat-label">{SUBCATEGORIES_LABEL}</h2>
                    <div className="wk-grid wk-cat-subs">
                        {subs.map((sub) => (
                            <button
                                key={sub.id}
                                type="button"
                                className="wk-card wk-card--interactive wk-cat-sub"
                                data-testid="wiki-category-sub-card"
                                onClick={() => navigate(wikiPaths.category(sub.id))}
                            >
                                <span className="wk-cat-sub-row">
                                    <WikiIcon name="folder" size={16} className="wk-cat-sub-icon" />
                                    <span className="wk-t-name wk-cat-sub-name wk-ellipsis">{sub.name}</span>
                                    <span className="wk-t-meta wk-cat-sub-count">{plural(sub.page_count || 0, 'page')}</span>
                                </span>
                                {sub.description && <span className="wk-t-meta wk-cat-sub-desc">{sub.description}</span>}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {accessOpen && (
                <WikiAccessPanel
                    scope="category"
                    scopeId={current.id}
                    name={current.name}
                    parentId={isSub ? top.id : null}
                    parentName={isSub ? top.name : ''}
                    onClose={() => setAccessOpen(false)}
                />
            )}

            <section className="wk-cat-section" aria-labelledby="wk-cat-pages-label" data-testid="wiki-category-pages">
                <h2 id="wk-cat-pages-label" className="wk-t-label wk-cat-label">{pagesLabel(isSub, top)}</h2>
                {pagesQuery.isLoading && (
                    <div className="wk-card"><WikiLoading label="Loading pages…" /></div>
                )}
                {pagesQuery.isError && (
                    <div className="wk-card"><WikiError error={pagesQuery.error} onRetry={() => pagesQuery.refetch()} /></div>
                )}
                {pagesQuery.isSuccess && pages.length > 0 && (
                    <ul className="wk-card wk-card--flush wk-cat-rows" data-testid="wiki-category-page-list">
                        {pages.map((page) => (
                            <li key={page.page_id}>
                                <PageRow page={page} onOpen={() => navigate(wikiPaths.page(page.page_id))} />
                            </li>
                        ))}
                    </ul>
                )}
                {pagesQuery.isSuccess && pages.length === 0 && (
                    <div className="wk-cat-empty" data-testid="wiki-category-empty">
                        <p className="wk-t-name">{EMPTY_TITLE}</p>
                        <p className="wk-t-dense wk-cat-empty-body">{EMPTY_BODY}</p>
                        {canCreatePage && (
                            <button type="button" className="wk-btn wk-btn--primary wk-cat-empty-btn" onClick={openNewPage}>
                                <WikiIcon name="plus" size={14} />
                                New page
                            </button>
                        )}
                    </div>
                )}
            </section>
        </WikiFrame>
    );
}

/**
 * One page row (prototype `pageRow`): file icon · name · v{n} · Required /
 * Draft pills | Acknowledged / Needs you | owner | updated.
 */
function PageRow({ page, onOpen }) {
    const ackable = isAckable(page);
    return (
        <button type="button" className="wk-cat-row" data-testid="wiki-category-page-row" data-page-id={page.page_id} onClick={onOpen}>
            <span className="wk-cat-row-main">
                <WikiIcon name="file-text" size={16} className="wk-cat-row-icon" />
                <span className="wk-cat-row-name">{page.title || 'Untitled page'}</span>
                <span className="wk-t-meta wk-cat-row-v">{versionLabel(pageVersion(page))}</span>
                {page.required_ack && <span className="wk-pill is-outline">Required</span>}
                {page.status === 'draft' && <span className="wk-pill">Draft</span>}
            </span>
            <span className="wk-cat-row-read">
                {ackable && (page.acknowledged
                    ? <span className="wk-pill wk-pill--green">Acknowledged</span>
                    : <span className="wk-pill wk-pill--orange">Needs you</span>)}
            </span>
            <span className="wk-t-meta wk-cat-row-owner">{page.owner_name || ''}</span>
            <span className="wk-t-meta wk-cat-row-updated">{dateLabel(page.updated_at)}</span>
        </button>
    );
}
